import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";

export interface VisitTotals {
  today: number;
  last_7_days: number;
  last_30_days: number;
  total: number;
}

export interface DailyVisit {
  day: string;
  visits: number;
}

export interface TopPage {
  path: string;
  visits: number;
}

export interface TopArticle {
  id: number;
  title: string;
  slug: string;
  category: string;
  views: number;
}

const EMPTY_TOTALS: VisitTotals = {
  today: 0,
  last_7_days: 0,
  last_30_days: 0,
  total: 0,
};

/** Normaliza la fila que devuelven get_visit_totals / get_unique_visitor_totals. */
function parseTotals(data: unknown): VisitTotals | null {
  const t = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null;
  if (!t) return null;
  return {
    today: Number(t.today) || 0,
    last_7_days: Number(t.last_7_days) || 0,
    last_30_days: Number(t.last_30_days) || 0,
    total: Number(t.total) || 0,
  };
}

/**
 * Carga las estadísticas de visitas del sitio y las noticias más leídas.
 * Requiere haber ejecutado el script `supabase/sql/analytics_setup.sql`.
 */
export function useVisitStats(days = 30) {
  const [totals, setTotals] = useState<VisitTotals>(EMPTY_TOTALS);
  /** Visitantes únicos por período; null si la función aún no existe en Supabase. */
  const [uniques, setUniques] = useState<VisitTotals | null>(null);
  const [daily, setDaily] = useState<DailyVisit[]>([]);
  const [topPages, setTopPages] = useState<TopPage[]>([]);
  const [topArticles, setTopArticles] = useState<TopArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** true cuando el error indica que aún falta ejecutar el SQL en Supabase. */
  const [needsSetup, setNeedsSetup] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNeedsSetup(false);
    try {
      // Se piden muchas rutas para poder extraer también las noticias
      // (/articulo/<slug>) del mismo rango de fechas.
      const [totalsRes, dailyRes, pagesRes, uniquesRes] = await Promise.all([
        supabase.rpc("get_visit_totals"),
        supabase.rpc("get_daily_visits", { p_days: days }),
        supabase.rpc("get_top_pages", { p_days: days, p_limit: 5000 }),
        // Opcional: si falla, el resto de las estadísticas se muestra igual.
        supabase.rpc("get_unique_visitor_totals"),
      ]);

      const firstError = totalsRes.error || dailyRes.error || pagesRes.error;
      if (firstError) {
        // Código PGRST202 / "function ... does not exist" = falta correr el SQL.
        const msg = firstError.message || "";
        if (
          firstError.code === "PGRST202" ||
          /function|does not exist|schema cache/i.test(msg)
        ) {
          setNeedsSetup(true);
        }
        throw firstError;
      }

      const allPages: TopPage[] = (pagesRes.data || []).map(
        (p: { path: string; visits: number }) => ({
          path: p.path,
          visits: Number(p.visits) || 0,
        }),
      );

      // Lecturas por noticia dentro del rango seleccionado.
      const viewsBySlug = new Map<string, number>();
      for (const p of allPages) {
        if (!p.path.startsWith("/articulo/")) continue;
        let slug = p.path.slice("/articulo/".length).replace(/\/+$/, "");
        try {
          slug = decodeURIComponent(slug);
        } catch {
          // Ruta mal codificada: se usa tal cual.
        }
        if (!slug) continue;
        viewsBySlug.set(slug, (viewsBySlug.get(slug) || 0) + p.visits);
      }
      const topSlugs = [...viewsBySlug.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10);

      let rankedArticles: TopArticle[] = [];
      if (topSlugs.length > 0) {
        const articlesRes = await supabase
          .from("articles")
          .select("id, title, slug, category")
          .in(
            "slug",
            topSlugs.map(([slug]) => slug),
          );
        if (articlesRes.error) throw articlesRes.error;
        const bySlug = new Map((articlesRes.data || []).map((a) => [a.slug, a]));
        rankedArticles = topSlugs.flatMap(([slug, views]) => {
          const a = bySlug.get(slug);
          // Noticias borradas o slugs que ya no existen no se muestran.
          return a
            ? [{ id: a.id, title: a.title, slug: a.slug, category: a.category, views }]
            : [];
        });
      }

      setTotals(parseTotals(totalsRes.data) ?? EMPTY_TOTALS);
      setUniques(uniquesRes.error ? null : parseTotals(uniquesRes.data));
      setDaily(
        (dailyRes.data || []).map((d: { day: string; visits: number }) => ({
          day: d.day,
          visits: Number(d.visits) || 0,
        })),
      );
      // Cada noticia es una ruta distinta y por separado nunca entra en el
      // ranking; se agrupan todas en una sola fila "/articulo".
      const articlesTotal = [...viewsBySlug.values()].reduce((s, v) => s + v, 0);
      const sectionPages = allPages.filter((p) => !p.path.startsWith("/articulo/"));
      if (articlesTotal > 0) {
        sectionPages.push({ path: "/articulo", visits: articlesTotal });
      }
      sectionPages.sort((a, b) => b.visits - a.visits);
      setTopPages(sectionPages.slice(0, 10));
      setTopArticles(rankedArticles);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Error al cargar las estadísticas",
      );
      setTotals(EMPTY_TOTALS);
      setUniques(null);
      setDaily([]);
      setTopPages([]);
      setTopArticles([]);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    totals,
    uniques,
    daily,
    topPages,
    topArticles,
    loading,
    error,
    needsSetup,
    refetch: fetchStats,
  };
}
